import { AppType } from "../../../api/models";
import { appInfo, isAdmin } from "../../../authConfig";

export type Product = {
    manual_key: string;
    faq_key: string;
    text: string;
    versions: ProductVersion[];
    is_sub_only?: string;
    faq_groupids: string[];
    manual_groupids: string[];
};

export type SubProduct = {
    manual_sub_key: string;
    faq_sub_key: string;
    text: string;
};

export type ProductVersion = {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
    sub_versions: SubProduct[];
    faq_sub_key?: string;
};

type ProductFilterSelection = {
    manual_key: string | string[];
    faq_key: string | string[];
};

type ScalarProductFilterSelection = {
    manual_key: string;
    faq_key: string;
};

/**
 * Gets product details from answer context
 */
export const getProductDetailsFromAppInfo = (answer: any, appName: string, selectedLocale: string): Product => {
    let targetKeyValue: string | undefined | Array<string>;

    const extractKeys = (response: any, appName: string) => {
        if (!response?.context?.product_filter) return null;

        const filter = response.context.product_filter;
        return appName === "ALL"
            ? [filter.faq_key, filter.manual_key].filter(Boolean)
            : appName === "FAQ" && answer["FAQ"]
              ? filter["faq_key"]
              : appName === "Manuals" && answer["Manuals"]
                ? filter["manual_key"]
                : [filter.faq_key, filter.manual_key].filter(Boolean);
    };

    const responses =
        appName === "ALL"
            ? [answer["ALL"], answer["FAQ"], answer["Manuals"]]
            : appName === "FAQ"
              ? [answer["FAQ"], answer["ALL"], answer["Manuals"]]
              : [answer["Manuals"], answer["ALL"], answer["FAQ"]];

    for (const response of responses) {
        if (response) {
            const keys = extractKeys(response, appName);
            if (keys || (Array.isArray(keys) && keys.length > 0)) {
                targetKeyValue = keys;
                break;
            }
        }
    }

    const filteredSelectedProduct = appInfo.product_categories[selectedLocale]?.filter((item: any) => {
        if (appName === "ALL") {
            return Array.isArray(targetKeyValue)
                ? targetKeyValue.filter((value: string) => !!(value && value !== "")).includes(item.faq_key) ||
                      targetKeyValue.filter((value: string) => !!(value && value !== "")).includes(item.manual_key)
                : targetKeyValue === item.faq_key || targetKeyValue === item.manual_key;
        } else {
            const keyToCheck = appName === "FAQ" ? "faq_key" : "manual_key";
            return Array.isArray(targetKeyValue)
                ? targetKeyValue.filter((value: string) => !!(value && value !== "")).includes(item.faq_key) ||
                      targetKeyValue.filter((value: string) => !!(value && value !== "")).includes(item.manual_key)
                : item[keyToCheck] === targetKeyValue;
        }
    })[0];

    return filteredSelectedProduct;
};

/**
 * Sets product filter for request payload
 */
export const setProductFilterForRequestPayload = (
    answer: any,
    appName: string,
    currentView: string,
    productIndexes: { manual_key: string; faq_key: string },
    selectedLocale: string
): ProductFilterSelection | null => {
    let productFilterValue: ProductFilterSelection | null = { manual_key: "0", faq_key: "0" };

    if (answer !== undefined && answer !== null) {
        if (currentView === "chat") {
            const fallbackOrder =
                appName === "ALL"
                    ? ["ALL", "FAQ", "Manuals"]
                    : appName === "FAQ"
                      ? ["FAQ", "ALL", "Manuals"]
                      : appName === "Manuals"
                        ? ["Manuals", "ALL", "FAQ"]
                        : ["ALL", "FAQ", "Manuals"];

            productFilterValue = fallbackOrder.map(type => answer[type]?.context?.product_filter).find(filter => filter !== undefined && filter !== null) || {
                manual_key: "0",
                faq_key: "0"
            };
        } else {
            const filteredSelectedProduct: Product = getProductDetailsFromAppInfo(answer, appName, selectedLocale);
            productFilterValue = {
                manual_key: filteredSelectedProduct?.manual_key?.toString() ?? "0",
                faq_key: filteredSelectedProduct?.faq_key?.toString() ?? "0"
            };
        }
    }

    return answer !== undefined && answer !== null
        ? productFilterValue
        : (productIndexes.manual_key === "0" && productIndexes.faq_key === "0") || (productIndexes.manual_key === "" && productIndexes.faq_key === "")
          ? isAdmin
              ? { manual_key: "0", faq_key: "0" }
              : appInfo.product_indexes
          : productIndexes;
};

/**
 * Sets sub-product filter for request payload
 */
export const setSubProductFilterForRequestPayload = (
    answer: any,
    appName: string,
    currentView: string,
    subProductIndexes: { manual_sub_key: string; faq_sub_key: string },
    selectedLocale: string
): { manual_sub_key: string; faq_sub_key: string } | null => {
    let subProductFilterValue: { manual_sub_key: string; faq_sub_key: string } | null = {
        manual_sub_key: "0",
        faq_sub_key: "0"
    };

    if (answer !== undefined && answer !== null) {
        if (currentView === "chat") {
            const fallbackOrder: Record<string, string[]> = {
                ALL: ["ALL", "FAQ", "Manuals"],
                FAQ: ["FAQ", "ALL", "Manuals"],
                Manuals: ["Manuals", "ALL", "FAQ"]
            };

            subProductFilterValue = (fallbackOrder[appName] || fallbackOrder["ALL"])
                .map((type: string) => answer[type]?.context?.sub_product_filter)
                .find((filter: any) => filter !== undefined && filter !== null) || {
                manual_sub_key: "0",
                faq_sub_key: "0"
            };
        } else {
            const filteredSelectedProduct: Product = getProductDetailsFromAppInfo(answer, appName, selectedLocale);
            const filteredVersion: ProductVersion | undefined = filteredSelectedProduct?.versions?.find((item: ProductVersion) => {
                const fallbackOrder =
                    appName === "ALL"
                        ? ["ALL", "FAQ", "Manuals"]
                        : appName === "FAQ"
                          ? ["FAQ", "ALL", "Manuals"]
                          : appName === "Manuals"
                            ? ["Manuals", "ALL", "FAQ"]
                            : ["ALL", "FAQ", "Manuals"];

                return fallbackOrder.some(type => {
                    const response = answer[type];
                    if (!response?.context?.productVersion) return false;

                    const versionKeys = response.context.productVersion;
                    return (
                        (!!(item.faq_version_key && item.faq_version_key !== "") && item.faq_version_key === versionKeys.faq_version_key) ||
                        (!!(item.manual_version_key && item.manual_version_key !== "") && item.manual_version_key === versionKeys.manual_version_key)
                    );
                });
            });

            const filteredSubProduct: any | undefined = (
                filteredSelectedProduct?.is_sub_only && filteredSelectedProduct.is_sub_only === "true"
                    ? filteredSelectedProduct.versions
                    : filteredVersion?.sub_versions
            )?.find((item: any) => {
                const fallbackOrder =
                    appName === "ALL"
                        ? ["ALL", "FAQ", "Manuals"]
                        : appName === "FAQ"
                          ? ["FAQ", "ALL", "Manuals"]
                          : appName === "Manuals"
                            ? ["Manuals", "ALL", "FAQ"]
                            : ["ALL", "FAQ", "Manuals"];

                return fallbackOrder.some(type => {
                    const subFilter = answer[type]?.context?.sub_product_filter;
                    return (
                        subFilter &&
                        ((!!(item.faq_sub_key && item.faq_sub_key !== "") && item.faq_sub_key === subFilter.faq_sub_key) ||
                            (!!(item.manual_sub_key && item.manual_sub_key !== "") && item.manual_sub_key === subFilter.manual_sub_key))
                    );
                });
            });

            subProductFilterValue = {
                manual_sub_key: filteredSubProduct?.manual_sub_key?.toString() ?? "0",
                faq_sub_key: filteredSubProduct?.faq_sub_key?.toString() ?? "0"
            };
        }
    }

    return answer !== undefined && answer !== null ? subProductFilterValue : subProductIndexes;
};

/**
 * Sets product version for request payload
 */
export const setProductVersionForRequestPayload = (
    answer: any,
    appName: string,
    currentView: string,
    productVersionIndexes: { manual_version_key: string; faq_version_key: string },
    selectedLocale: string
): { manual_version_key: string; faq_version_key: string } | null => {
    let productVersionValue: { manual_version_key: string; faq_version_key: string } | null = {
        manual_version_key: "0",
        faq_version_key: "0"
    };

    if (answer !== undefined && answer !== null) {
        if (currentView === "chat") {
            const fallbackOrder: Record<string, string[]> = {
                ALL: ["ALL", "FAQ", "Manuals"],
                FAQ: ["FAQ", "ALL", "Manuals"],
                Manuals: ["Manuals", "ALL", "FAQ"]
            };

            productVersionValue = (fallbackOrder[appName] || fallbackOrder.ALL)
                .map((type: string) => answer[type]?.context?.productVersion)
                .find((version: any) => version !== undefined && version !== null) || {
                manual_version_key: "0",
                faq_version_key: "0"
            };
        } else {
            const filteredSelectedProduct: Product = getProductDetailsFromAppInfo(answer, appName, selectedLocale);
            const filteredVersion: ProductVersion | undefined = filteredSelectedProduct?.versions?.find((item: ProductVersion) => {
                const searchOrder = appName === "ALL" ? ["ALL", "FAQ", "Manuals"] : appName === "FAQ" ? ["FAQ", "ALL", "Manuals"] : ["Manuals", "ALL", "FAQ"];

                return searchOrder.some(type => {
                    const versionKeys = answer[type]?.context?.productVersion;
                    return (
                        versionKeys &&
                        ((!!(item.faq_version_key && item.faq_version_key !== "") && item.faq_version_key === versionKeys.faq_version_key) ||
                            (!!(item.manual_version_key && item.manual_version_key !== "") && item.manual_version_key === versionKeys.manual_version_key))
                    );
                });
            });

            productVersionValue = {
                manual_version_key: filteredVersion?.manual_version_key?.toString() ?? "0",
                faq_version_key: filteredVersion?.faq_version_key?.toString() ?? "0"
            };
        }
    }

    return answer !== undefined && answer !== null ? productVersionValue : productVersionIndexes;
};

/**
 * Sets group IDs by app type for request payload
 */
export const setGroupIdsByAppTypeForRequestPayload = (
    appType: AppType,
    product_filter: ProductFilterSelection | null | string[],
    sub_product_filter: { manual_sub_key: string; faq_sub_key: string } | null,
    selectedLocale: string
): { appType: AppType; groupIds: Set<string>; sub_group_ids: Set<string> | undefined } | undefined => {
    if (product_filter && typeof product_filter === "object" && !Array.isArray(product_filter)) {
        // When product_filter values are arrays (the product_indexes dict shape from appInfo),
        // no single product is selected — return undefined so callers apply no group-ID restriction.
        if (Array.isArray((product_filter as any).faq_key) || Array.isArray((product_filter as any).manual_key)) {
            return undefined;
        }
        const scalarProductFilter = product_filter as ScalarProductFilterSelection;
        const targetKeyValues =
            appType === "ALL" ? [scalarProductFilter.faq_key, scalarProductFilter.manual_key].filter(value => !!(value && value !== "")) : undefined;
        const targetKeyValue = appType === "FAQ" ? scalarProductFilter.faq_key : scalarProductFilter.manual_key;

        const filteredSelectedProduct: Product = appInfo.product_categories[selectedLocale]?.filter((item: any) =>
            appType === "ALL"
                ? !!targetKeyValues && (targetKeyValues.includes(item.faq_key) || targetKeyValues.includes(item.manual_key))
                : appType === "FAQ"
                  ? item.faq_key === targetKeyValue
                  : item.manual_key === targetKeyValue
        )[0];

        let targetProductIndexes: Set<string> = new Set();
        let targetGroupIds: Set<string> = new Set();

        if (
            JSON.stringify({ manual_key: filteredSelectedProduct.manual_key, faq_key: filteredSelectedProduct.faq_key }) !==
            JSON.stringify({ manual_key: "0", faq_key: "0" })
        ) {
            targetProductIndexes =
                appType === "ALL"
                    ? new Set([filteredSelectedProduct?.faq_key, filteredSelectedProduct?.manual_key].filter(value => !!(value && value !== "")))
                    : new Set([appType === "FAQ" ? filteredSelectedProduct?.faq_key : filteredSelectedProduct?.manual_key]);

            if (filteredSelectedProduct?.is_sub_only === "true") {
                if (
                    JSON.stringify({ manual_sub_key: sub_product_filter?.manual_sub_key, faq_sub_key: sub_product_filter?.faq_sub_key }) ===
                        JSON.stringify({ manual_sub_key: "0", faq_sub_key: "0" }) ||
                    JSON.stringify({ manual_sub_key: sub_product_filter?.manual_sub_key, faq_sub_key: sub_product_filter?.faq_sub_key }) ===
                        JSON.stringify({ manual_sub_key: appType === "Manuals" ? "0" : "", faq_sub_key: appType === "FAQ" ? "0" : "" }) ||
                    (appType === "ALL" &&
                        JSON.stringify({ manual_sub_key: sub_product_filter?.manual_sub_key, faq_sub_key: sub_product_filter?.faq_sub_key }) ===
                            JSON.stringify({ manual_sub_key: "0", faq_sub_key: "" })) ||
                    JSON.stringify({ manual_sub_key: sub_product_filter?.manual_sub_key, faq_sub_key: sub_product_filter?.faq_sub_key }) ===
                        JSON.stringify({ manual_sub_key: "", faq_sub_key: "0" })
                ) {
                    targetGroupIds =
                        appType === "ALL"
                            ? new Set(
                                  filteredSelectedProduct?.faq_groupids
                                      .concat(filteredSelectedProduct?.manual_groupids)
                                      .filter(value => !!(value && value !== ""))
                              )
                            : new Set(
                                  (appType === "FAQ" ? filteredSelectedProduct?.faq_groupids : filteredSelectedProduct?.manual_groupids).filter(
                                      value => !!(value && value !== "")
                                  )
                              );
                    return { appType: appType, groupIds: targetGroupIds, sub_group_ids: undefined };
                } else {
                    targetProductIndexes =
                        appType === "ALL"
                            ? new Set(
                                  filteredSelectedProduct?.faq_groupids
                                      .concat(filteredSelectedProduct?.manual_groupids)
                                      .filter(value => !!(value && value !== ""))
                              )
                            : new Set(
                                  (appType === "FAQ" ? filteredSelectedProduct?.faq_groupids : filteredSelectedProduct?.manual_groupids).filter(
                                      value => !!(value && value !== "")
                                  )
                              );

                    const selectedSubProduct = filteredSelectedProduct?.versions?.filter(
                        (subProduct: any) =>
                            (subProduct.manual_sub_key && subProduct.manual_sub_key === sub_product_filter?.manual_sub_key) ||
                            (subProduct.faq_sub_key && subProduct.faq_sub_key === sub_product_filter?.faq_sub_key)
                    )[0];

                    const targetSubGroupIds: Set<string> = new Set();

                    targetGroupIds = new Set([
                        ...appInfo.product_categories[selectedLocale]
                            .filter((item: any) => {
                                if (
                                    (appType === "ALL" && (targetProductIndexes.has(item.faq_key) || targetProductIndexes.has(item.manual_key))) ||
                                    targetProductIndexes.has(appType === "FAQ" ? item.faq_key : item.manual_key)
                                ) {
                                    if (item.is_sub_only === "true") {
                                        const targetSubProducts = item?.versions?.filter((subProduct: any) =>
                                            appType === "ALL"
                                                ? !!(subProduct.faq_sub_key && subProduct.faq_sub_key !== "") ||
                                                  !!(subProduct.manual_sub_key && subProduct.manual_sub_key !== "")
                                                : appType === "FAQ"
                                                  ? !!(subProduct.faq_sub_key && subProduct.faq_sub_key !== "")
                                                  : !!(subProduct.manual_sub_key && subProduct.manual_sub_key !== "")
                                        );

                                        if (targetSubProducts?.length === 0) {
                                            return true;
                                        } else {
                                            const subproducts = targetSubProducts?.filter((subProduct: any) => {
                                                if (subProduct?.text === selectedSubProduct?.text) {
                                                    if (appType === "ALL") {
                                                        targetSubGroupIds.add(subProduct.faq_sub_key);
                                                        targetSubGroupIds.add(subProduct.manual_sub_key);
                                                    } else {
                                                        targetSubGroupIds.add(appType === "FAQ" ? subProduct.faq_sub_key : subProduct.manual_sub_key);
                                                    }
                                                    return true;
                                                }
                                                return false;
                                            });
                                            return subproducts && subproducts.length > 0;
                                        }
                                    } else {
                                        return false;
                                    }
                                }
                            })
                            .map((item: any) => {
                                return appType === "ALL"
                                    ? [item.faq_key, item.manual_key].filter(value => !!(value && value !== ""))
                                    : appType === "FAQ"
                                      ? item.faq_key
                                      : item.manual_key;
                            })
                            .flat()
                    ]);

                    return { appType: appType, groupIds: targetGroupIds, sub_group_ids: targetSubGroupIds };
                }
            } else {
                return {
                    appType: appType,
                    groupIds:
                        appType === "ALL"
                            ? new Set(
                                  filteredSelectedProduct?.faq_groupids
                                      .concat(filteredSelectedProduct?.manual_groupids)
                                      .filter(value => !!(value && value !== ""))
                              )
                            : new Set(
                                  (appType === "FAQ" ? filteredSelectedProduct?.faq_groupids : filteredSelectedProduct?.manual_groupids).filter(
                                      value => !!(value && value !== "")
                                  )
                              ),
                    sub_group_ids:
                        appType === "ALL"
                            ? new Set(
                                  [sub_product_filter?.faq_sub_key || "", sub_product_filter?.manual_sub_key || ""].filter(value => !!(value && value !== ""))
                              )
                            : new Set(
                                  [appType === "FAQ" ? sub_product_filter?.faq_sub_key || "" : sub_product_filter?.manual_sub_key || ""].filter(
                                      value => !!(value && value !== "")
                                  )
                              )
                };
            }
        } else {
            return undefined;
        }
    }
};
